import { ViewBooking } from "collins-js-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../ui/accordion";
import { DiaryBookingCard } from "../../Sidebar/DiaryBookingCardList/DiaryBookingCard/DiaryBookingCard";

interface RunsheetBookingListProps {
  groupedBookings: { [key: string]: ViewBooking[] };
  expandedGroups: Set<string>;
  onExpandedGroupsChange: (groups: Set<string>) => void;
  createItemData: (booking: ViewBooking) => any;
}

// Constants for virtualization
const DEFAULT_ACCORDION_ITEM_HEIGHT = 42; // Default collapsed height
const BUFFER_SIZE = 2;

// Simple virtual scrolling implementation with dynamic height measurement
const VirtualAccordionList = React.memo(
  ({
    accordionItems,
    renderAccordionItem,
    height,
    expandedGroups
  }: {
    accordionItems: Array<{ time: string; bookings: ViewBooking[] }>;
    renderAccordionItem: (
      item: (typeof accordionItems)[0]
    ) => React.ReactElement;
    height: number;
    expandedGroups: Set<string>;
  }) => {
    const [scrollTop, setScrollTop] = React.useState(0);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const itemHeights = React.useRef<Map<number, number>>(new Map());
    const itemRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Measure item heights
    const measureItemHeight = useCallback(
      (index: number, element: HTMLDivElement | null) => {
        if (element) {
          itemRefs.current.set(index, element);
          const height = element.getBoundingClientRect().height;
          if (height > 0) {
            itemHeights.current.set(index, height);
          }
        }
      },
      []
    );

    // Calculate cumulative heights for positioning
    const getCumulativeHeight = useCallback((upToIndex: number) => {
      let totalHeight = 0;
      for (let i = 0; i < upToIndex; i++) {
        const height =
          itemHeights.current.get(i) ?? DEFAULT_ACCORDION_ITEM_HEIGHT;
        totalHeight += height;
      }
      return totalHeight;
    }, []);

    // Get total height of all items
    const getTotalHeight = useCallback(() => {
      return getCumulativeHeight(accordionItems.length);
    }, [accordionItems.length, getCumulativeHeight]);

    // Find the first visible item based on scroll position
    const getStartIndex = useCallback(() => {
      let cumulativeHeight = 0;
      for (let i = 0; i < accordionItems.length; i++) {
        const itemHeight =
          itemHeights.current.get(i) ?? DEFAULT_ACCORDION_ITEM_HEIGHT;
        if (cumulativeHeight + itemHeight + 8 > scrollTop) {
          return Math.max(0, i - BUFFER_SIZE);
        }
        cumulativeHeight += itemHeight + 8;
      }
      return Math.max(0, accordionItems.length - 1);
    }, [scrollTop, accordionItems.length]);

    // Find the last visible item based on scroll position and viewport height
    const getEndIndex = useCallback(
      (startIndex: number) => {
        let cumulativeHeight = getCumulativeHeight(startIndex);
        const targetHeight = scrollTop + height;

        for (let i = startIndex; i < accordionItems.length; i++) {
          const itemHeight =
            itemHeights.current.get(i) ?? DEFAULT_ACCORDION_ITEM_HEIGHT;
          if (cumulativeHeight > targetHeight) {
            return Math.min(accordionItems.length, i + BUFFER_SIZE);
          }
          cumulativeHeight += itemHeight + 8;
        }
        return accordionItems.length;
      },
      [scrollTop, height, accordionItems.length, getCumulativeHeight]
    );

    // Calculate visible range
    const startIndex = getStartIndex();
    const endIndex = getEndIndex(startIndex);
    const offsetY = getCumulativeHeight(startIndex);
    const visibleItems = accordionItems.slice(startIndex, endIndex);
    const totalHeight = getTotalHeight();

    // Update heights when expanded groups change
    React.useEffect(() => {
      // Force remeasurement after state changes
      const timer = setTimeout(() => {
        itemRefs.current.forEach((element, index) => {
          const height = element.getBoundingClientRect().height;
          if (height > 0) {
            itemHeights.current.set(index, height);
          }
        });
      }, 0);

      return () => clearTimeout(timer);
    }, [expandedGroups]);

    return (
      <div
        ref={containerRef}
        style={{
          height,
          overflow: "auto",
          position: "relative"
        }}
        onScroll={handleScroll}
      >
        <div
          style={{
            height: totalHeight,
            position: "relative"
          }}
        >
          <div
            style={{
              position: "absolute",
              top: offsetY,
              left: 0,
              right: 0
            }}
          >
            {visibleItems.map((item, index) => {
              const actualIndex = startIndex + index;
              return (
                <div
                  key={actualIndex}
                  style={{ paddingBottom: "8px" }}
                  ref={(el) => measureItemHeight(actualIndex, el)}
                >
                  {renderAccordionItem(item)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

VirtualAccordionList.displayName = "VirtualAccordionList";

export const RunsheetBookingList: React.FC<RunsheetBookingListProps> = ({
  groupedBookings,
  expandedGroups,
  onExpandedGroupsChange,
  createItemData
}) => {
  const accordionItems = useMemo(
    () =>
      Object.entries(groupedBookings).map(([time, bookings]) => ({
        time,
        bookings
      })),
    [groupedBookings]
  );

  const renderAccordionItem = useCallback(
    ({ time, bookings }: (typeof accordionItems)[0]) => (
      <AccordionItem
        key={time}
        value={time}
        className="w-full RunSheet_Beta_AccordionItem mb-2 border-t border-border bg-card overflow-hidden last:mb-0"
      >
        <AccordionTrigger className="RunSheet_Beta_AccordionTrigger RunSheet_Beta_AccordionTrigger--condensed hover:no-underline">
          <div className="RunSheet_Beta_TimeGroup flex items-center justify-between w-full h-5">
            <div className="RunSheet_Beta_TimeGroup__left flex items-center gap-2">
              <span className="RunSheet_Beta_TimeGroup__time text-base font-bold text-foreground leading-none">
                {time}
              </span>
              <span
                className="RunSheet_Beta_TimeGroup__count text-[11px] font-medium text-muted-foreground bg-border-color px-1.5 py-[3px] rounded leading-none whitespace-nowrap"
                data-count={bookings.length}
              >
                {bookings.length}
              </span>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="RunSheet_Beta_AccordionContent RunSheet_Beta_AccordionContent--condensed p-0 m-0 border-none rounded-none bg-transparent overflow-hidden">
          <div className="space-y-2">
            {bookings.map((booking, bookingIndex) => {
              const itemData = createItemData(booking);

              return (
                <div
                  key={
                    booking.id ?? booking.offlineId ?? `booking-${bookingIndex}`
                  }
                  className="RunSheet_Beta_CardWrapper border-none rounded-none m-0 bg-transparent h-auto min-h-[auto] w-full block overflow-visible relative z-[1]"
                >
                  <DiaryBookingCard
                    index={0}
                    style={{
                      position: "relative",
                      height: "auto"
                    }}
                    parent={{ props: itemData }}
                  />
                </div>
              );
            })}
          </div>
        </AccordionContent>
      </AccordionItem>
    ),
    [createItemData]
  );

  // Calculate viewport height with proper CSS variable handling
  const calculateViewportHeight = useCallback(() => {
    const windowHeight = window.innerHeight;
    const styles = getComputedStyle(document.documentElement);
    const tabAdjustment = parseInt(
      styles.getPropertyValue("--workspace-tab-adjustment") || "0",
      10
    );
    const safeAreaTop = parseInt(
      styles.getPropertyValue("--safe-area-inset-top") || "0",
      10
    );
    return windowHeight - 200 - tabAdjustment - safeAreaTop;
  }, []);

  const [viewportHeight, setViewportHeight] = useState(calculateViewportHeight);

  // Update height on window resize (important for webview orientation changes)
  useEffect(() => {
    const handleResize = () => setViewportHeight(calculateViewportHeight());

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateViewportHeight]);

  return (
    <div className="RunSheet_Beta_BookingList">
      <Accordion
        type="multiple"
        value={Array.from(expandedGroups)}
        onValueChange={(value) => onExpandedGroupsChange(new Set(value))}
        className="w-full RunSheet_Beta_Accordion"
      >
        <VirtualAccordionList
          accordionItems={accordionItems}
          renderAccordionItem={renderAccordionItem}
          height={viewportHeight}
          expandedGroups={expandedGroups}
        />
      </Accordion>
    </div>
  );
};
